import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  FiActivity,
  FiAlertCircle,
  FiCalendar,
  FiChevronRight,
  FiClipboard,
  FiDollarSign,
  FiDownload,
  FiMapPin,
  FiMessageSquare,
  FiPhone,
  FiRefreshCw,
  FiSearch,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Card, PageHeader } from '../components/common/index.js';
import {
  clearCustomerDetail,
  fetchCustomerDetail,
  fetchCustomers,
} from '../redux/slices/customerSlice';
import { customerApi } from '../api/customerApi';
import {
  PERIOD_LABEL,
  downloadCustomerWorkbook,
  downloadCustomersWorkbook,
} from '../utils/customerExcel';

// The report window — the same chips the rest of the panel uses, plus a
// custom from→to range. Whatever is set here is EXACTLY what the Excel holds.
const PERIODS = ['today', 'week', 'month', 'year', 'all', 'custom'];

const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// Same variant codes the sales site and the walk-in entry app store.
const VARIANT_LABELS = {
  pmt: 'Petrol Manual',
  pat: 'Petrol Automatic',
  dmt: 'Diesel Manual',
  dat: 'Diesel Automatic',
  ev: 'Electric (EV)',
};

const CUSTOMER_STATE_CONFIG = {
  pending: { label: 'Pending', pill: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400' },
  active: { label: 'Active', pill: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  closed: { label: 'Closed', pill: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400' },
};

const INQUIRY_STATUS_CONFIG = {
  new: { label: 'New', pill: 'bg-violet-100 text-violet-700', dot: 'bg-violet-500' },
  pending: { label: 'Pending', pill: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400' },
  replied: { label: 'Replied', pill: 'bg-sky-100 text-sky-700', dot: 'bg-sky-500' },
  confirmed: { label: 'Confirmed', pill: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-500' },
  completed: { label: 'Completed', pill: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  closed: { label: 'Closed', pill: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400' },
};

const STATE_TABS = [
  { value: 'all', label: 'All customers' },
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
];

const formatPrice = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const amount = Number(value);
  return Number.isNaN(amount) ? value : amount.toLocaleString('en-IN');
};

const initials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
};

const StateBadge = ({ state }) => {
  const config = CUSTOMER_STATE_CONFIG[state] || {
    label: state || 'Unknown',
    pill: 'bg-slate-100 text-slate-600',
    dot: 'bg-slate-400',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${config.pill}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
};

const InquiryStatusBadge = ({ status }) => {
  const config = INQUIRY_STATUS_CONFIG[status] || {
    label: status || 'Unknown',
    pill: 'bg-slate-100 text-slate-600',
    dot: 'bg-slate-400',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${config.pill}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, iconClass, iconBackground }) => (
  <div className="flex min-w-0 items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconBackground}`}>
      <Icon className={`h-5 w-5 ${iconClass}`} />
    </div>
    <div className="min-w-0">
      <p className="truncate text-xs font-medium text-slate-400">{label}</p>
      <p className="text-xl font-bold leading-tight text-slate-900">{value}</p>
    </div>
  </div>
);

const InquiryCard = ({ inquiry }) => {
  const price = formatPrice(inquiry.quoted_price);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
        <div className="min-w-0">
          <div className="mb-1.5 flex flex-wrap items-center gap-2">
            <p className="text-sm font-bold text-slate-900">Inquiry #{inquiry.id}</p>
            <InquiryStatusBadge status={inquiry.status} />
            {inquiry.priority && (
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${
                inquiry.priority === 'urgent'
                  ? 'bg-rose-100 text-rose-700'
                  : 'bg-slate-100 text-slate-600'
              }`}>
                {inquiry.priority}
              </span>
            )}
          </div>
          <p className="text-sm font-semibold text-slate-700">
            {[inquiry.car_brand, inquiry.car_model].filter(Boolean).join(' ') || 'Vehicle not specified'}
          </p>
          {/* Year and variant the customer actually asked about — the same part
              differs across them, so sourcing needs both. Both are optional, so
              the row disappears entirely when neither was captured. */}
          {(inquiry.car_year || inquiry.car_variant) && (
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {inquiry.car_year && (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-600">
                  {inquiry.car_year}
                </span>
              )}
              {inquiry.car_variant && (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-600">
                  {VARIANT_LABELS[inquiry.car_variant] || String(inquiry.car_variant).toUpperCase()}
                </span>
              )}
            </div>
          )}
          <p className="mt-1 text-xs leading-relaxed text-slate-500">{inquiry.car_parts || 'No parts listed'}</p>
        </div>
        <div className="shrink-0 sm:text-right">
          <p className="text-xs text-slate-400">Quoted price</p>
          <p className={`text-base font-bold ${price ? 'text-emerald-600' : 'text-slate-300'}`}>
            {price ? `₹${price}` : 'Not quoted'}
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 border-t border-slate-100 pt-4 sm:grid-cols-2">
        <div className="rounded-xl bg-slate-50 p-3">
          <p className="mb-1 text-xs text-slate-400">Salesperson</p>
          <p className="text-sm font-semibold text-slate-700">{inquiry.salesperson_name || '—'}</p>
        </div>
        <div className="rounded-xl bg-slate-50 p-3">
          <p className="mb-1 text-xs text-slate-400">Quoted by</p>
          <p className="text-sm font-semibold text-slate-700">{inquiry.quoted_by || '—'}</p>
        </div>
      </div>

      {(inquiry.notes || inquiry.supervisor_notes) && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {inquiry.notes && (
            <div className="rounded-xl border border-amber-100 bg-amber-50 p-3">
              <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-amber-700">
                <FiMessageSquare className="h-3.5 w-3.5" /> Customer note
              </p>
              <p className="text-xs leading-relaxed text-slate-600">{inquiry.notes}</p>
            </div>
          )}
          {inquiry.supervisor_notes && (
            <div className="rounded-xl border border-violet-100 bg-violet-50 p-3">
              <p className="mb-1 text-xs font-semibold text-violet-700">Supervisor note</p>
              <p className="text-xs leading-relaxed text-slate-600">{inquiry.supervisor_notes}</p>
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
        <span>{inquiry.created_at}</span>
        {inquiry.quoted_at && <span>Quoted {inquiry.quoted_at}</span>}
      </div>
    </div>
  );
};

const CustomerDetailModal = ({ detail, loading, error, onClose, onDownload, downloading }) => {
  const customer = detail?.customer;
  const inquiries = detail?.inquiries ?? [];
  const statusCounts = detail?.status_counts ?? customer?.inquiry_stats ?? {};

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="Customer details">
      <button className="absolute inset-0 cursor-default bg-slate-900/60 backdrop-blur-sm" onClick={onClose} aria-label="Close customer details" />
      <div className="relative h-full w-full max-w-2xl overflow-y-auto bg-slate-50 shadow-2xl animate-fade-in">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white/95 px-5 py-4 backdrop-blur sm:px-6">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-violet-600">Customer details</p>
            <h2 className="truncate text-lg font-bold text-slate-900">{customer?.name || 'Loading…'}</h2>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {customer && (
              <button
                onClick={() => onDownload?.(customer.id)}
                disabled={downloading}
                className="flex items-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60"
              >
                <FiDownload className={`h-4 w-4 ${downloading ? 'animate-pulse' : ''}`} />
                {downloading ? 'Building…' : 'Excel'}
              </button>
            )}
            <button onClick={onClose} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700" aria-label="Close">
              <FiX className="h-5 w-5" />
            </button>
          </div>
        </div>

        {loading && (
          <div className="flex flex-col items-center justify-center gap-3 py-24">
            <div className="h-9 w-9 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
            <p className="text-sm text-slate-400">Loading customer history…</p>
          </div>
        )}

        {error && !loading && (
          <div className="m-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 sm:m-6">{error}</div>
        )}

        {customer && !loading && (
          <div className="space-y-5 p-5 sm:p-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 text-lg font-bold text-white shadow-sm">
                  {initials(customer.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-xl font-bold text-slate-900">{customer.name || 'Unnamed customer'}</h3>
                    <StateBadge state={detail.inquiry_state || customer.inquiry_state} />
                  </div>
                  <div className="mt-3 grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
                    <a href={`tel:${customer.phone_number}`} className="flex items-center gap-2 hover:text-violet-600">
                      <FiPhone className="h-4 w-4 text-slate-400" /> {customer.phone_number || '—'}
                    </a>
                    <span className="flex items-center gap-2">
                      <FiMapPin className="h-4 w-4 text-slate-400" /> {customer.location || 'Location not provided'}
                    </span>
                    <span className="flex items-center gap-2 sm:col-span-2">
                      <FiCalendar className="h-4 w-4 text-slate-400" /> Customer since {customer.created_at || '—'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Inquiry overview</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  ['Total', detail.total_inquiries ?? customer.inquiry_count ?? 0, 'text-slate-900'],
                  ['Pending', statusCounts.pending ?? 0, 'text-amber-600'],
                  ['Active', customer.inquiry_stats?.open ?? (detail.inquiry_state_counts?.active ?? 0), 'text-emerald-600'],
                  ['Completed', statusCounts.completed ?? 0, 'text-indigo-600'],
                ].map(([label, value, color]) => (
                  <div key={label} className="rounded-xl border border-slate-200 bg-white p-3 text-center shadow-sm">
                    <p className={`text-xl font-bold ${color}`}>{value}</p>
                    <p className="text-xs text-slate-400">{label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-3 flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Inquiry history</p>
                <span className="rounded-full bg-violet-100 px-2.5 py-1 text-xs font-bold text-violet-700">{inquiries.length}</span>
              </div>
              {inquiries.length ? (
                <div className="space-y-3">
                  {inquiries.map((inquiry) => <InquiryCard key={inquiry.id} inquiry={inquiry} />)}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-10 text-center">
                  <FiClipboard className="mx-auto mb-2 h-6 w-6 text-slate-300" />
                  <p className="text-sm font-semibold text-slate-600">No inquiries for this customer</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CustomerCard = ({ customer, onOpen }) => (
  <button onClick={() => onOpen(customer)} className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-violet-200 hover:shadow-md lg:hidden">
    <div className="flex items-start gap-3">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 text-sm font-bold text-white">
        {initials(customer.name)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-slate-900">{customer.name || 'Unnamed customer'}</p>
            <p className="mt-0.5 text-xs text-slate-500">{customer.phone_number || '—'}</p>
          </div>
          <StateBadge state={customer.inquiry_state} />
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
          <span className="flex items-center gap-1.5 text-xs text-slate-500">
            <FiMapPin className="h-3.5 w-3.5" /> {customer.location || 'No location'}
          </span>
          <span className="flex items-center gap-1 text-xs font-semibold text-violet-600">
            {customer.inquiry_count ?? 0} inquiries <FiChevronRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </div>
  </button>
);

const CustomersPage = () => {
  const dispatch = useDispatch();
  const { list, total, loading, error, detail, detailLoading, detailError } = useSelector((state) => state.customers);
  const [search, setSearch] = useState('');
  const [activeState, setActiveState] = useState('all');
  const [detailOpen, setDetailOpen] = useState(false);
  // The money picture comes from its own period-aware report, so the list
  // (who exists) and the report (what they spent) stay independent.
  // Today by default — the morning question is "what happened today".
  const [period, setPeriod] = useState('today');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  // Every fetch and every download speaks in these same params, so the Excel
  // can never cover a different window than the screen.
  const windowParams = useMemo(() => {
    if (period === 'custom') {
      const params = {};
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      return params;
    }
    return { period };
  }, [period, dateFrom, dateTo]);

  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  useEffect(() => {
    if (period === 'custom' && !dateFrom && !dateTo) return undefined;
    let alive = true;
    setReportLoading(true);
    setReportError('');
    customerApi.getReport(windowParams)
      .then((res) => { if (alive) setReport(res.data); })
      .catch((e) => {
        if (alive) setReportError(e?.response?.data?.error || 'Could not load customer money');
      })
      .finally(() => { if (alive) setReportLoading(false); });
    return () => { alive = false; };
  }, [windowParams, period, dateFrom, dateTo]);

  // customer_id → their money for the selected period
  const moneyById = useMemo(() => {
    const map = {};
    (report?.customers || []).forEach((r) => { map[r.customer_id] = r; });
    return map;
  }, [report]);

  const downloadAll = () => {
    if (!report) return;
    // If a search is typed, the file matches what the admin is looking at —
    // the matched customers and ONLY their inquiries and parts.
    const query = search.trim().toLowerCase();
    if (!query) {
      downloadCustomersWorkbook(report, period);
      return;
    }
    const match = (c) => [c.customer_id, c.name, c.phone, c.location, c.added_by]
      .filter((v) => v !== null && v !== undefined).join(' ').toLowerCase().includes(query);
    const kept = (report.customers || []).filter(match);
    const keptIds = new Set(kept.map((c) => c.customer_id));
    downloadCustomersWorkbook({
      ...report,
      customers: kept,
      inquiries: (report.inquiries || []).filter((r) => keptIds.has(r.customer_id)),
      parts: (report.parts || []).filter((r) => keptIds.has(r.customer_id)),
    }, period);
  };

  const downloadOne = async (customerId) => {
    // "Date range" with no dates picked has no window yet — downloading now
    // would quietly build a TODAY file while the screen shows the old one.
    if (period === 'custom' && !dateFrom && !dateTo) {
      setReportError('Pick the date range first — from, to, or both.');
      return;
    }
    setDownloadingId(customerId);
    try {
      const res = await customerApi.getOneReport(customerId, windowParams);
      downloadCustomerWorkbook(res.data, period);
    } catch (e) {
      setReportError(e?.response?.data?.error || 'Could not build that customer file');
    } finally {
      setDownloadingId(null);
    }
  };

  const stateCounts = useMemo(() => ({
    all: list.length,
    pending: list.filter((customer) => customer.inquiry_state === 'pending').length,
    active: list.filter((customer) => customer.inquiry_state === 'active').length,
    closed: list.filter((customer) => customer.inquiry_state === 'closed').length,
  }), [list]);

  const totalInquiries = useMemo(
    () => list.reduce((sum, customer) => sum + (Number(customer.inquiry_count) || 0), 0),
    [list]
  );

  const filteredCustomers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return list.filter((customer) => {
      const matchesState = activeState === 'all' || customer.inquiry_state === activeState;
      if (!matchesState) return false;
      if (!query) return true;
      const searchable = [
        customer.id,
        customer.name,
        customer.phone_number,
        customer.location,
        customer.created_by,
      ].filter((value) => value !== null && value !== undefined).join(' ').toLowerCase();
      return searchable.includes(query);
    });
  }, [activeState, list, search]);

  const openDetail = (customer) => {
    setDetailOpen(true);
    dispatch(fetchCustomerDetail(customer.id));
  };

  const closeDetail = () => {
    setDetailOpen(false);
    dispatch(clearCustomerDetail());
  };

  return (
    <MainLayout>
      <div className="space-y-5 sm:space-y-6">
        <PageHeader meta={loading && !list.length ? 'Loading…' : `${total} total customers`}>
          <button onClick={() => dispatch(fetchCustomers())} disabled={loading} className="flex self-start items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-60 sm:self-auto">
            <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button
            onClick={downloadAll}
            disabled={!report || reportLoading
              || (period === 'custom' && !dateFrom && !dateTo)}
            title={`Download every customer — ${PERIOD_LABEL[period]}`}
            className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60"
          >
            <FiDownload className="h-4 w-4" /> Excel
          </button>
        </PageHeader>

        {/* The window every money number below is measured over */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Money for</span>
          <div className="flex items-center gap-1 rounded-xl bg-slate-100 p-1">
            {PERIODS.map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
                  period === p ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {p === 'custom' && <FiCalendar className="h-3 w-3" />}
                {PERIOD_LABEL[p]}
              </button>
            ))}
          </div>
          {period === 'custom' && (
            <div className="flex items-center gap-2">
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-xl border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 focus:border-violet-400 focus:outline-none" />
              <span className="text-xs text-slate-400">to</span>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="rounded-xl border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 focus:border-violet-400 focus:outline-none" />
              {(dateFrom || dateTo) && (
                <button type="button" onClick={() => { setDateFrom(''); setDateTo(''); }}
                  className="text-xs font-semibold text-slate-400 transition hover:text-rose-500">
                  Clear
                </button>
              )}
            </div>
          )}
          {reportLoading && <span className="text-xs text-slate-400">loading…</span>}
          {report?.window?.from && (
            <span className="text-xs text-slate-400">
              {report.window.from} → {report.window.to}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <StatCard icon={FiUsers} label="Total customers" value={total} iconBackground="bg-violet-50" iconClass="text-violet-600" />
          <StatCard icon={FiActivity} label={`Bought · ${PERIOD_LABEL[period]}`} value={report?.totals?.buying_customers ?? '—'} iconBackground="bg-emerald-50" iconClass="text-emerald-600" />
          <StatCard icon={FiClipboard} label="Total inquiries" value={totalInquiries} iconBackground="bg-sky-50" iconClass="text-sky-600" />
          <StatCard icon={FiDollarSign} label="Given to company" value={report ? money(report.totals?.collected) : '—'} iconBackground="bg-emerald-50" iconClass="text-emerald-600" />
          <StatCard icon={FiAlertCircle} label={`Owed by ${report?.totals?.owing_customers ?? 0}`} value={report ? money(report.totals?.balance) : '—'} iconBackground="bg-amber-50" iconClass="text-amber-600" />
        </div>

        {reportError && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">{reportError}</div>
        )}

        <div className="relative animate-fade-in-up">
          <FiSearch className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(event) => setSearch(event.target.value)} type="search" placeholder="Search by name, phone number, location or customer ID…" className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-10 text-sm text-slate-700 placeholder-slate-400 transition-all focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300" />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label="Clear search">
              <FiX className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-1 overflow-x-auto pb-0.5 animate-fade-in-up">
          {STATE_TABS.map(({ value, label }) => (
            <button key={value} onClick={() => setActiveState(value)} className={`flex items-center gap-1.5 whitespace-nowrap rounded-xl px-3 py-2 text-sm font-semibold transition-all ${activeState === value ? 'bg-violet-600 text-white shadow-sm' : 'border border-slate-200 bg-white text-slate-500 hover:text-slate-700'}`}>
              {label}
              <span className={`rounded-full px-1.5 py-0.5 text-xs font-bold ${activeState === value ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>{stateCounts[value] ?? 0}</span>
            </button>
          ))}
        </div>

        {error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

        <Card title="Customer list" subtitle={`Showing ${filteredCustomers.length} of ${total}`}>
          {loading && !list.length ? (
            <div className="flex flex-col items-center justify-center gap-3 py-20">
              <div className="h-9 w-9 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
              <p className="text-sm text-slate-400">Loading customers…</p>
            </div>
          ) : !filteredCustomers.length ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-50">
                <FiUsers className="h-7 w-7 text-slate-400" />
              </div>
              <p className="mb-1 font-semibold text-slate-800">No customers found</p>
              <p className="text-sm text-slate-400">{search ? 'Try a different search term.' : `No ${activeState === 'all' ? '' : `${activeState} `}customers available.`}</p>
            </div>
          ) : (
            <>
              <div className="space-y-3 lg:hidden">
                {filteredCustomers.map((customer) => <CustomerCard key={customer.id} customer={customer} onOpen={openDetail} />)}
              </div>
              <div className="-mx-6 hidden overflow-x-auto px-6 lg:block">
                <table className="w-full min-w-[820px]">
                  <thead>
                    <tr className="border-b border-slate-100">
                      {['Customer', 'Contact', 'Location', 'Inquiries', 'Bought', 'Given', 'Balance', 'State', ''].map((heading) => (
                        <th key={heading} className="pb-3 pr-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">{heading}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {filteredCustomers.map((customer) => {
                      const m = moneyById[customer.id] || {};
                      const owes = Number(m.balance || 0) > 0;
                      return (
                      <tr key={customer.id} onClick={() => openDetail(customer)} className={`cursor-pointer transition-colors ${owes ? 'bg-amber-50/40 hover:bg-amber-50' : 'hover:bg-slate-50/70'}`}>
                        <td className="py-3.5 pr-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 text-xs font-bold text-white">{initials(customer.name)}</div>
                            <div>
                              <p className="text-sm font-semibold text-slate-800">{customer.name || 'Unnamed customer'}</p>
                              <p className="text-xs text-slate-400">ID #{customer.id} · joined {customer.created_at?.split(' at ')[0] || '—'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3.5 pr-4 text-sm text-slate-600">{customer.phone_number || '—'}</td>
                        <td className="py-3.5 pr-4 text-sm capitalize text-slate-600">{customer.location || '—'}</td>
                        <td className="py-3.5 pr-4">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700">
                            <FiClipboard className="h-3 w-3" /> {customer.inquiry_count ?? 0}
                          </span>
                        </td>
                        {/* Money for the selected period — sold value, what
                            actually reached us, and what is still outstanding */}
                        <td className="py-3.5 pr-4 text-sm font-semibold text-slate-700">
                          {m.sold_deals ? money(m.sold_value) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="py-3.5 pr-4 text-sm font-bold text-emerald-600">
                          {m.sold_deals ? money(m.collected) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="py-3.5 pr-4">
                          {owes ? (
                            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-700">
                              {money(m.balance)}
                            </span>
                          ) : <span className="text-sm text-slate-300">—</span>}
                        </td>
                        <td className="py-3.5 pr-4"><StateBadge state={customer.inquiry_state} /></td>
                        <td className="py-3.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); downloadOne(customer.id); }}
                              disabled={downloadingId === customer.id}
                              title="Download this customer's full file"
                              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-50"
                            >
                              <FiDownload className={`h-4 w-4 ${downloadingId === customer.id ? 'animate-pulse' : ''}`} />
                            </button>
                            <FiChevronRight className="h-4 w-4 text-slate-300" />
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      </div>

      {detailOpen && (
        <CustomerDetailModal
          detail={detail}
          loading={detailLoading}
          error={detailError}
          onClose={closeDetail}
          onDownload={downloadOne}
          downloading={downloadingId === detail?.customer?.id}
        />
      )}
    </MainLayout>
  );
};

export default CustomersPage;
